import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import {
  UngeneratedEmployee,
  fmt, fmtMonth, THIS_MONTH, THIS_SESSION,
  IcoChevL, IcoChevR, IcoRefresh, IcoFilter, IcoX,
  IcoDollar, IcoCheck, IcoList, IcoClock, IcoAlert, IcoZap,
  SkeletonRow, Avatar, CSS, SHELL_CSS,
} from './PayrollManagement';
import GeneratePayrollModal from './parts/GeneratePayrollModal';
import PayrollAdvances from './parts/PayrollAdvances';
import { printHtml, buildPayslipHtml, buildPayrollRegisterHtml } from './parts/PrintService';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';

type RecordsTab = 'records' | 'advances';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface PaginationData {
  current_page: number;
  total_pages:  number;
  total_items:  number;
  per_page:     number;
  has_next:     boolean;
  has_previous: boolean;
}

export interface PayrollRecord {
  id: number;
  employee_id: number;
  employee_role: 'teacher' | 'staff' | 'principal';
  salary_id: number;
  month_year: string;
  session: string;
  basic_salary: number;
  hra: number;
  transport_allowance: number;
  medical_allowance: number;
  other_allowance: number;
  gross_salary: number;
  working_days: number;
  present_days: number;
  leave_days: number;
  provident_fund: number;
  professional_tax: number;
  tds: number;
  other_deductions: number;
  total_deductions: number;
  net_salary: number;
  payment_status: 'pending' | 'paid' | 'on_hold' | 'cancelled';
  payment_mode: 'cash' | 'bank_transfer' | 'cheque' | null;
  payment_date: string | null;
  transaction_ref: string | null;
  generated_by: number;
  remarks: string | null;
  created_at: string;
  updated_at: string;
  employee_name: string | null;
  profile_picture?: string | null;
}

interface PaymentForm {
  payment_mode:    'cash' | 'bank_transfer' | 'cheque' | '';
  payment_date:    string;
  transaction_ref: string;
}

interface AttendanceDay {
  date:       string;
  day:        string;
  day_num:    number;
  is_sunday:  boolean;
  is_present: boolean;
  is_future:  boolean;
}

interface AttendancePreview {
  employee_id:   number;
  employee_role: string;
  employee_name: string;
  month_year:    string;
  working_days:  number;
  present_days:  number;
  leave_days:    number;
  days:          AttendanceDay[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline SVG
// ─────────────────────────────────────────────────────────────────────────────
const Ico = ({ d, size = 16, color = 'currentColor', style = {}, strokeWidth = 1.8 }: {
  d: string | string[]; size?: number; color?: string; style?: React.CSSProperties; strokeWidth?: number;
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'block', ...style }}>
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);

const IcoTrash    = (p: any) => <Ico {...p} d={['M3 6h18', 'M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2']} />;
const IcoEdit     = (p: any) => <Ico {...p} d={['M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7', 'M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z']} />;
const IcoChevDown = (p: any) => <Ico {...p} d="M6 9l6 6 6-6" />;
const IcoCalendar = (p: any) => <Ico {...p} d={['M19 4H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2z', 'M16 2v4', 'M8 2v4', 'M3 10h18']} />;
const IcoArrowR   = (p: any) => <Ico {...p} d={['M5 12h14', 'M12 5l7 7-7 7']} />;
const IcoArrowL   = (p: any) => <Ico {...p} d={['M19 12H5', 'M12 19l-7-7 7-7']} />;
const IcoPlus     = (p: any) => <Ico {...p} d={['M12 5v14', 'M5 12h14']} />;
const IcoUserX    = (p: any) => <Ico {...p} d={['M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2', 'M8.5 11a4 4 0 100-8 4 4 0 000 8z', 'M17 8l5 5', 'M22 8l-5 5']} />;
const IcoPrinter  = (p: any) => <Ico {...p} d={['M6 9V2h12v7', 'M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2', 'M6 14h12v8H6z']} />;

// ─────────────────────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: PayrollRecord['payment_status'] }> = ({ status }) => {
  const map: Record<PayrollRecord['payment_status'], { cls: string; icon: React.ReactNode; label: string }> = {
    paid:      { cls: 'pr-badge--paid',      icon: <IcoCheck size={9} color="#15803d" />, label: 'Paid' },
    pending:   { cls: 'pr-badge--pending',   icon: <IcoClock size={9} color="#b45309" />, label: 'Pending' },
    on_hold:   { cls: 'pr-badge--on_hold',   icon: <IcoAlert size={9} color="#4338ca" />, label: 'On hold' },
    cancelled: { cls: 'pr-badge--cancelled', icon: <IcoAlert size={9} color="#dc2626" />, label: 'Cancelled' },
  };
  const m = map[status] ?? map.pending;
  return <span className={`pr-badge ${m.cls}`}>{m.icon} {m.label}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Attendance Calendar — selectable absent days
// ─────────────────────────────────────────────────────────────────────────────
const AttendanceCalendar: React.FC<{
  preview:        AttendancePreview;
  selectedDates:  Set<string>;
  deselectedDates: Set<string>;
  onToggle:       (date: string) => void;
  onSelectAllAbsent: (dates: string[]) => void;
  onDeselectAllPresent: (dates: string[]) => void;
  onClearSelected:   () => void;
  onClearDeselected: () => void;
  onResetAll:        () => void;
  isEdit?:        boolean;
}> = ({
  preview, selectedDates, deselectedDates, onToggle,
  onSelectAllAbsent, onDeselectAllPresent, onClearSelected, onClearDeselected, onResetAll,
  isEdit = false,
}) => {
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const firstDate   = new Date(preview.month_year + '-01');
  const startOffset = (firstDate.getDay() + 6) % 7;

  const effectivePresent = preview.present_days + selectedDates.size - deselectedDates.size;
  const attendPct = preview.working_days > 0
    ? Math.round((effectivePresent / preview.working_days) * 100) : 0;

  const absentClickable  = preview.days.filter(d => !d.is_sunday && !d.is_future && !d.is_present).map(d => d.date);
  const presentClickable = preview.days.filter(d => !d.is_sunday && !d.is_future && d.is_present).map(d => d.date);

  const allAbsentSelected    = absentClickable.length  > 0 && absentClickable.every(d  => selectedDates.has(d));
  const allPresentDeselected = presentClickable.length > 0 && presentClickable.every(d => deselectedDates.has(d));
  const hasAnyChange = selectedDates.size > 0 || deselectedDates.size > 0;

  const bulkBtnStyle: React.CSSProperties = {
    padding: '5px 11px', borderRadius: 8, fontSize: 11, fontWeight: 700,
    cursor: 'pointer', border: '1px solid transparent', fontFamily: "'DM Sans', sans-serif",
    display: 'inline-flex', alignItems: 'center', gap: 5,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
        {[
          { label: 'Working',    val: preview.working_days,                            color: '#374151', bg: '#f9fafb' },
          { label: 'Present',    val: Math.max(0, effectivePresent),                   color: '#15803d', bg: 'rgba(34,197,94,.07)' },
          { label: 'Absent',     val: preview.working_days - Math.max(0, effectivePresent), color: '#dc2626', bg: 'rgba(239,68,68,.07)' },
          { label: 'Attendance', val: `${Math.max(0, attendPct)}%`, color: attendPct >= 75 ? '#15803d' : '#dc2626', bg: attendPct >= 75 ? 'rgba(34,197,94,.07)' : 'rgba(239,68,68,.07)' },
        ].map(s => (
          <div key={s.label} style={{ padding: '10px 12px', borderRadius: 10, background: s.bg, border: '1px solid #e5e7eb', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', marginTop: 2, textTransform: 'uppercase', letterSpacing: '.4px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Instruction */}
      <div style={{ fontSize: 12, color: '#6b7280', background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.2)', borderRadius: 8, padding: '8px 12px', fontWeight: 500 }}>
        {isEdit
          ? <>Tap <strong>absent days</strong> to add or <strong>present days</strong> to remove from this payroll.</>
          : <>Tap any <strong>absent day</strong> to include it in this payment. Salary will be recalculated on the next step.</>
        }
      </div>

      {/* Bulk actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          disabled={absentClickable.length === 0}
          onClick={() => allAbsentSelected ? onClearSelected() : onSelectAllAbsent(absentClickable)}
          style={{
            ...bulkBtnStyle,
            background: allAbsentSelected ? 'rgba(245,158,11,.12)' : 'rgba(245,158,11,.08)',
            color: '#b45309', borderColor: 'rgba(245,158,11,.25)',
            opacity: absentClickable.length === 0 ? 0.4 : 1,
            cursor: absentClickable.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {allAbsentSelected ? 'Deselect all absent' : 'Select all absent'}
        </button>

        {isEdit && (
          <button
            type="button"
            disabled={presentClickable.length === 0}
            onClick={() => allPresentDeselected ? onClearDeselected() : onDeselectAllPresent(presentClickable)}
            style={{
              ...bulkBtnStyle,
              background: allPresentDeselected ? 'rgba(239,68,68,.1)' : 'rgba(239,68,68,.06)',
              color: '#dc2626', borderColor: 'rgba(239,68,68,.2)',
              opacity: presentClickable.length === 0 ? 0.4 : 1,
              cursor: presentClickable.length === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            {allPresentDeselected ? 'Restore all present' : 'Mark all present as absent'}
          </button>
        )}

        {hasAnyChange && (
          <button
            type="button"
            onClick={onResetAll}
            style={{ ...bulkBtnStyle, background: '#f3f4f6', color: '#6b7280', borderColor: '#e5e7eb', marginLeft: 'auto' }}
          >
            Reset changes
          </button>
        )}
      </div>

      {/* Calendar */}
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 4 }}>
          {DAYS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 700, color: d === 'Sun' ? '#dc2626' : '#9ca3af', padding: '4px 0', textTransform: 'uppercase', letterSpacing: '.5px' }}>
              {d}
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
          {Array.from({ length: startOffset }).map((_, i) => <div key={`e-${i}`} />)}
          {preview.days.map(day => {
            const isSelected   = selectedDates.has(day.date);
            const isDeselected = deselectedDates.has(day.date);

            const effectivelyPresent = (day.is_present && !isDeselected) || isSelected;
            const isClickable = !day.is_sunday && !day.is_future;

            let bg = '#f9fafb', color = '#374151', border = '1px solid #e5e7eb';
            let dotColor: string | null = null;

            if (day.is_sunday) {
              bg = 'rgba(239,68,68,.04)'; color = '#fca5a5'; border = '1px solid rgba(239,68,68,.1)';
            } else if (day.is_future) {
              bg = '#f9fafb'; color = '#d1d5db'; border = '1px solid #f3f4f6';
            } else if (isDeselected) {
              bg = 'rgba(239,68,68,.07)'; color = '#dc2626'; border = '2px dashed #ef4444'; dotColor = '#ef4444';
            } else if (isSelected) {
              bg = 'rgba(245,158,11,.12)'; color = '#b45309'; border = '2px solid #f59e0b'; dotColor = '#f59e0b';
            } else if (day.is_present) {
              bg = 'rgba(34,197,94,.1)'; color = '#15803d'; border = '1px solid rgba(34,197,94,.25)'; dotColor = '#22c55e';
            } else {
              bg = 'rgba(239,68,68,.07)'; color = '#dc2626'; border = '1px solid rgba(239,68,68,.18)'; dotColor = '#ef4444';
            }

            return (
              <div key={day.date}
                onClick={() => isClickable && onToggle(day.date)}
                title={
                  !isClickable ? undefined
                    : isDeselected ? 'Click to restore'
                    : isSelected ? 'Click to deselect'
                    : day.is_present ? (isEdit ? 'Click to remove from payroll' : undefined)
                    : 'Click to include in payment'
                }
                style={{ borderRadius: 8, background: bg, border, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '6px 2px', gap: 3, minHeight: 44, cursor: isClickable ? 'pointer' : 'default', transition: 'all .12s', position: 'relative' }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color }}>{day.day_num}</span>
                {dotColor && <div style={{ width: 5, height: 5, borderRadius: '50%', background: dotColor }} />}

                {isClickable && !day.is_present && !isSelected && (
                  <div style={{ position: 'absolute', top: 2, right: 2, width: 12, height: 12, borderRadius: '50%', background: 'rgba(239,68,68,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IcoPlus size={8} color="#dc2626" strokeWidth={2.5} />
                  </div>
                )}
                {isSelected && (
                  <div style={{ position: 'absolute', top: 2, right: 2, width: 12, height: 12, borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IcoCheck size={7} color="white" strokeWidth={3} />
                  </div>
                )}
                {isEdit && day.is_present && !isDeselected && (
                  <div style={{ position: 'absolute', top: 2, right: 2, width: 12, height: 12, borderRadius: '50%', background: 'rgba(34,197,94,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ico d="M5 12h14" size={8} color="#15803d" strokeWidth={2.5} />
                  </div>
                )}
                {isDeselected && (
                  <div style={{ position: 'absolute', top: 2, right: 2, width: 12, height: 12, borderRadius: '50%', background: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Ico d={['M6 6l6 6', 'M12 6l-6 6']} size={7} color="white" strokeWidth={2.5} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          { color: '#22c55e', bg: 'rgba(34,197,94,.1)',   label: 'Present' },
          { color: '#ef4444', bg: 'rgba(239,68,68,.07)',  label: 'Absent' },
          { color: '#f59e0b', bg: 'rgba(245,158,11,.12)', label: 'Added' },
          ...(isEdit ? [{ color: '#ef4444', bg: 'rgba(239,68,68,.07)', label: 'Removed', dashed: true }] : []),
        ].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: l.bg, border: `${(l as any).dashed ? '2px dashed' : '1px solid'} ${l.color}60` }} />
            <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>{l.label}</span>
          </div>
        ))}
      </div>

      {(selectedDates.size > 0 || deselectedDates.size > 0) && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.25)', fontSize: 13, color: '#b45309', fontWeight: 600, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {selectedDates.size > 0 && <span>+{selectedDates.size} day{selectedDates.size > 1 ? 's' : ''} added</span>}
          {deselectedDates.size > 0 && <span>−{deselectedDates.size} day{deselectedDates.size > 1 ? 's' : ''} removed</span>}
          <span style={{ color: '#6b7280', fontWeight: 500 }}>· salary recalculated on next step</span>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared attendance-change → salary estimate math, used by SalaryRecap and
// by PaymentModal (to prefill the manual override field with the same number).
// ─────────────────────────────────────────────────────────────────────────────
function computeSalaryRecap(
  record: PayrollRecord,
  preview: AttendancePreview | null,
  selectedDates: Set<string>,
  deselectedDates: Set<string>,
) {
  const hasChange = selectedDates.size > 0 || deselectedDates.size > 0;

  if (!preview || !hasChange) {
    return {
      hasChange,
      newPresent: record.present_days,
      newGross:   Number(record.gross_salary),
      newDeduct:  Number(record.total_deductions),
      newNet:     Number(record.net_salary),
    };
  }

  const origPresentDays = record.present_days;
  const newPresent = Math.max(
    0,
    Math.min(origPresentDays + selectedDates.size - deselectedDates.size, record.working_days),
  );

  const grossPerDay  = origPresentDays > 0 ? Number(record.gross_salary)     / origPresentDays : 0;
  const deductPerDay = origPresentDays > 0 ? Number(record.total_deductions) / origPresentDays : 0;

  const newGross  = Math.round(grossPerDay  * newPresent);
  const newDeduct = Math.round(deductPerDay * newPresent);
  const newNet    = newGross - newDeduct;

  return { hasChange, newPresent, newGross, newDeduct, newNet };
}

const SalaryRecap: React.FC<{
  record:          PayrollRecord;
  preview:         AttendancePreview | null;
  selectedDates:   Set<string>;
  deselectedDates: Set<string>;
}> = ({ record, preview, selectedDates, deselectedDates }) => {
  const { hasChange, newPresent, newGross, newDeduct, newNet } =
    computeSalaryRecap(record, preview, selectedDates, deselectedDates);

  if (!preview || !hasChange) {
    return (
      <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(59,130,246,.05)', border: '1px solid rgba(59,130,246,.15)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>Payment summary</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Gross salary</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{fmt(record.gross_salary)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Total deductions</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>−{fmt(record.total_deductions)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid rgba(59,130,246,.12)' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Net payable</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#1e40af' }}>{fmt(record.net_salary)}</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 16px', borderRadius: 12, background: 'rgba(245,158,11,.05)', border: '1px solid rgba(245,158,11,.2)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>
        Recalculated — {newPresent}/{record.working_days} days
        {selectedDates.size > 0 && ` (+${selectedDates.size})`}
        {deselectedDates.size > 0 && ` (−${deselectedDates.size})`}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Original net</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textDecoration: 'line-through' }}>{fmt(record.net_salary)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>New gross (est.)</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#1d4ed8' }}>{fmt(newGross)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Deductions (est.)</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#dc2626' }}>−{fmt(newDeduct)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid rgba(245,158,11,.2)' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>New net (est.)</span>
        <span style={{ fontSize: 16, fontWeight: 800, color: '#b45309' }}>{fmt(newNet)}</span>
      </div>
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>* Exact amount calculated by server using salary definition.</div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared 3-step payment modal — used by both MarkPaid and EditPaid
// ─────────────────────────────────────────────────────────────────────────────
const PaymentModal: React.FC<{
  record:   PayrollRecord;
  isEdit:   boolean;
  onDone:   () => void;
  onClose:  () => void;
  toast:    React.RefObject<Toast | null>;
}> = ({ record, isEdit, onDone, onClose, toast }) => {
  const [step,            setStep]           = useState<1 | 2 | 3>(1);
  const [previewChoice,   setPreviewChoice]  = useState<'ask' | 'preview' | 'skip'>('ask');
  const [preview,         setPreview]        = useState<AttendancePreview | null>(null);
  const [previewLoading,  setPreviewLoading] = useState(false);

  const [selectedDates,   setSelectedDates]   = useState<Set<string>>(new Set());
  const [deselectedDates, setDeselectedDates] = useState<Set<string>>(new Set());

  const [form,  setForm]  = useState<PaymentForm>({
    payment_mode:    (record.payment_mode ?? '') as any,
    payment_date:    record.payment_date ?? new Date().toISOString().substring(0, 10),
    transaction_ref: record.transaction_ref ?? '',
  });
  const [saving, setSaving] = useState(false);

  // Manual deduction/adjustment (flows into other_deductions, not a raw net override)
  // + free-text note — both optional, entered on the Review step
  const [adjustmentEnabled, setAdjustmentEnabled] = useState(false);
  const [deductionAmount,   setDeductionAmount]   = useState('');
  const [deductionReason,   setDeductionReason]    = useState('');
  const [note,              setNote]               = useState('');

  const setFormField = (k: keyof PaymentForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const toggleDate = (date: string) => {
    const day = preview?.days.find(d => d.date === date);
    if (!day) return;

    if (day.is_present) {
      if (!isEdit) return;
      setDeselectedDates(prev => { const n = new Set(prev); n.has(date) ? n.delete(date) : n.add(date); return n; });
    } else {
      setSelectedDates(prev => { const n = new Set(prev); n.has(date) ? n.delete(date) : n.add(date); return n; });
    }
  };

  const selectAllAbsent    = (dates: string[]) => setSelectedDates(new Set(dates));
  const deselectAllPresent = (dates: string[]) => setDeselectedDates(new Set(dates));
  const clearSelected      = () => setSelectedDates(new Set());
  const clearDeselected    = () => setDeselectedDates(new Set());
  const resetAllChanges    = () => { setSelectedDates(new Set()); setDeselectedDates(new Set()); };

  // Only fetch attendance once the person actually chooses to preview it —
  // skipping saves the request entirely and moves straight to Review/Payment.
  useEffect(() => {
    if (previewChoice !== 'preview') return;
    const load = async () => {
      setPreviewLoading(true);
      try {
        const res = await apiClient.get('/payroll/attendance-preview', {
          params: { employee_id: record.employee_id, employee_role: record.employee_role, month_year: record.month_year },
        });
        if (res.data.status === 'success') setPreview(res.data.data);
        else toast.current?.show({ severity: 'warn', summary: 'Warning', detail: res.data.message, life: 3000 });
      } catch {
        toast.current?.show({ severity: 'warn', summary: 'Warning', detail: 'Could not load attendance. You can still proceed.', life: 3000 });
      } finally { setPreviewLoading(false); }
    };
    load();
  }, [previewChoice]);

  // Use record.present_days as the authoritative base (matches SalaryRecap fix)
  const effectivePresent = Math.max(
    0,
    Math.min(
      record.present_days + selectedDates.size - deselectedDates.size,
      record.working_days,
    ),
  );

  const hasChange = selectedDates.size > 0 || deselectedDates.size > 0;

  // Same estimate SalaryRecap shows — used to prefill/display the manual override
  const recap = computeSalaryRecap(record, preview, selectedDates, deselectedDates);

  const handleSave = async () => {
    if (!form.payment_mode || !form.payment_date) {
      toast.current?.show({ severity: 'warn', summary: 'Missing', detail: 'Payment mode and date are required.', life: 3000 });
      return;
    }
    if (adjustmentEnabled && (deductionAmount === '' || isNaN(+deductionAmount) || +deductionAmount === 0)) {
      toast.current?.show({ severity: 'warn', summary: 'Missing', detail: 'Enter a non-zero adjustment amount, or turn it off.', life: 3000 });
      return;
    }
    setSaving(true);
    try {
      const endpoint = isEdit ? '/payroll/edit-payment' : '/payroll/mark-paid';
      const body: any = {
        payroll_record_id: record.id,
        payment_mode:      form.payment_mode,
        payment_date:      form.payment_date,
        transaction_ref:   form.transaction_ref || undefined,
      };

      if (hasChange) {
        body.present_days = effectivePresent;
      }
      if (adjustmentEnabled) {
        body.manual_deduction  = +deductionAmount;
        body.deduction_reason  = deductionReason.trim() || undefined;
      }
      if (note.trim()) {
        body.notes = note.trim();
      }

      const res = await apiClient.post(endpoint, body);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: isEdit ? 'Updated' : 'Paid',
          detail: `${isEdit ? 'Payment updated' : 'Marked as paid'} — ${fmt(res.data.net_salary ?? record.net_salary)}`,
          life: 3000,
        });
        onDone();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Failed', life: 4000 });
    } finally { setSaving(false); }
  };

  const STEPS = ['Attendance', 'Review', 'Payment'];
  const accentColor = isEdit ? '#6d28d9' : '#1e40af';
  const accentGrad  = isEdit ? 'linear-gradient(135deg,#6d28d9,#a78bfa)' : 'linear-gradient(135deg,#1e40af,#3b82f6)';

  return (
    <div className="pr-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pr-modal" style={{ maxWidth: step === 1 ? 600 : 460, transition: 'max-width .2s' }}>

        {/* Header */}
        <div className="pr-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar src={record.profile_picture ?? null} name={record.employee_name} size={36} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                {isEdit
                  ? step === 1 ? 'Edit attendance' : step === 2 ? 'Review changes' : 'Update payment'
                  : step === 1 ? 'Attendance preview' : step === 2 ? 'Review & recalculate' : 'Confirm payment'}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {record.employee_name ?? `#${record.employee_id}`} · {fmtMonth(record.month_year)} · {fmt(record.net_salary)}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {STEPS.map((_, idx) => (
                <div key={idx} style={{ height: 6, borderRadius: 3, transition: 'all .2s', width: idx + 1 === step ? 22 : 8, background: idx + 1 < step ? '#22c55e' : idx + 1 === step ? accentColor : '#e5e7eb' }} />
              ))}
            </div>
            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{step}/{STEPS.length}</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 8 }}>
              <IcoX size={17} color="#9ca3af" />
            </button>
          </div>
        </div>

        {/* Step 1 — Choice, then Calendar */}
        {step === 1 && (
          <>
            <div className="pr-modal-body">
              {previewChoice === 'ask' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '6px 0' }}>
                  <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                    Do you want to review and adjust attendance for <strong>{fmtMonth(record.month_year)}</strong> before {isEdit ? 'updating' : 'confirming'} this payment?
                    You can also skip straight ahead and use the existing figures.
                  </div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      className="pr-btn pr-btn--primary"
                      style={{ background: accentGrad, flex: 1, minWidth: 180, justifyContent: 'center' }}
                      onClick={() => setPreviewChoice('preview')}
                    >
                      <IcoCalendar size={13} color="white" /> Preview attendance
                    </button>
                    <button
                      className="pr-btn pr-btn--ghost"
                      style={{ flex: 1, minWidth: 180, justifyContent: 'center' }}
                      onClick={() => { setPreviewChoice('skip'); setStep(2); }}
                    >
                      Skip — use existing figures <IcoArrowR size={13} color="#1e40af" />
                    </button>
                  </div>
                </div>
              ) : previewLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                    {[0,1,2,3].map(i => <div key={i} className="pr-shimmer" style={{ height: 56, borderRadius: 10 }} />)}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
                    {Array.from({ length: 35 }).map((_, i) => <div key={i} className="pr-shimmer" style={{ height: 44, borderRadius: 8 }} />)}
                  </div>
                </div>
              ) : preview ? (
                <AttendanceCalendar
                  preview={preview}
                  selectedDates={selectedDates}
                  deselectedDates={deselectedDates}
                  onToggle={toggleDate}
                  onSelectAllAbsent={selectAllAbsent}
                  onDeselectAllPresent={deselectAllPresent}
                  onClearSelected={clearSelected}
                  onClearDeselected={clearDeselected}
                  onResetAll={resetAllChanges}
                  isEdit={isEdit}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '30px 0', color: '#9ca3af', fontSize: 13 }}>
                  Could not load attendance data. You can still proceed.
                </div>
              )}
            </div>
            <div className="pr-modal-footer">
              <button className="pr-btn pr-btn--ghost" onClick={onClose}>Cancel</button>
              {previewChoice !== 'ask' && (
                <button className="pr-btn pr-btn--primary" style={{ background: accentGrad }} onClick={() => setStep(2)}>
                  Review <IcoArrowR size={13} color="white" />
                </button>
              )}
            </div>
          </>
        )}

        {/* Step 2 — Review */}
        {step === 2 && (
          <>
            <div className="pr-modal-body">
              {/* Pills */}
              {preview && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ padding: '8px 14px', borderRadius: 9, background: 'rgba(34,197,94,.07)', border: '1px solid rgba(34,197,94,.2)', fontSize: 13, fontWeight: 700, color: '#15803d' }}>
                    ✓ {record.present_days} original
                  </div>
                  {selectedDates.size > 0 && (
                    <div style={{ padding: '8px 14px', borderRadius: 9, background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.2)', fontSize: 13, fontWeight: 700, color: '#b45309' }}>
                      +{selectedDates.size} added
                    </div>
                  )}
                  {deselectedDates.size > 0 && (
                    <div style={{ padding: '8px 14px', borderRadius: 9, background: 'rgba(239,68,68,.07)', border: '1px solid rgba(239,68,68,.2)', fontSize: 13, fontWeight: 700, color: '#dc2626' }}>
                      −{deselectedDates.size} removed
                    </div>
                  )}
                  <div style={{ padding: '8px 14px', borderRadius: 9, background: '#f9fafb', border: '1px solid #e5e7eb', fontSize: 13, fontWeight: 700, color: '#374151' }}>
                    / {record.working_days} working
                  </div>
                  <div style={{ padding: '8px 14px', borderRadius: 9, background: `rgba(${isEdit ? '109,28,209' : '59,130,246'},.08)`, border: `1px solid rgba(${isEdit ? '109,28,209' : '59,130,246'},.2)`, fontSize: 13, fontWeight: 700, color: accentColor }}>
                    = {effectivePresent} days paid
                  </div>
                </div>
              )}

              <SalaryRecap
                record={record}
                preview={preview}
                selectedDates={selectedDates}
                deselectedDates={deselectedDates}
              />

              {hasChange && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(30,58,138,.04)', border: '1px solid rgba(30,58,138,.12)', fontSize: 12, color: '#1e3a8a', fontWeight: 500 }}>
                  The payroll record will be updated with the recalculated figures.
                </div>
              )}

              {/* Manual deduction / adjustment — tracked in other_deductions, not a raw net override */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fafafa' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={adjustmentEnabled}
                    onChange={e => setAdjustmentEnabled(e.target.checked)}
                  />
                  Add a manual deduction or adjustment
                </label>
                {adjustmentEnabled && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div className="pr-field" style={{ marginBottom: 0 }}>
                        <label className="pr-label">Amount (₹)</label>
                        <input
                          type="number" className="pr-input" style={{ width: '100%' }}
                          placeholder="e.g. 500"
                          value={deductionAmount}
                          onChange={e => setDeductionAmount(e.target.value)}
                        />
                      </div>
                      <div className="pr-field" style={{ marginBottom: 0 }}>
                        <label className="pr-label">Reason (optional)</label>
                        <input
                          type="text" className="pr-input" style={{ width: '100%' }}
                          placeholder="e.g. Late fine, bonus…"
                          value={deductionReason}
                          onChange={e => setDeductionReason(e.target.value)}
                        />
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>
                      Positive = extra deduction, negative = a bonus / reduction. This adds to <strong>other deductions</strong> so the breakdown and net salary stay consistent.
                    </div>
                    {deductionAmount !== '' && !isNaN(+deductionAmount) && +deductionAmount !== 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
                        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Resulting net</span>
                        <span style={{ fontSize: 14, fontWeight: 800, color: accentColor }}>{fmt(recap.newNet - +deductionAmount)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Note */}
              <div className="pr-field" style={{ marginBottom: 0 }}>
                <label className="pr-label">Note (optional)</label>
                <textarea
                  className="pr-input"
                  style={{ width: '100%', minHeight: 60, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Add a general note about this payment…"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                />
              </div>
            </div>
            <div className="pr-modal-footer">
              <button
                className="pr-btn pr-btn--ghost"
                onClick={() => { if (previewChoice === 'skip') setPreviewChoice('ask'); setStep(1); }}
              >
                <IcoArrowL size={13} color="#1e40af" /> Back
              </button>
              <button className="pr-btn pr-btn--primary" style={{ background: accentGrad }} onClick={() => setStep(3)}>
                {isEdit ? 'Update payment' : 'Proceed to payment'} <IcoArrowR size={13} color="white" />
              </button>
            </div>
          </>
        )}

        {/* Step 3 — Payment details */}
        {step === 3 && (
          <>
            <div className="pr-modal-body">
              {/* Mini recap */}
              {(preview || adjustmentEnabled) && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: `rgba(${isEdit ? '109,28,209' : '59,130,246'},.05)`, border: `1px solid rgba(${isEdit ? '109,28,209' : '59,130,246'},.15)`, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  {preview && (
                    <div style={{ display: 'flex', gap: 5, alignItems: 'baseline' }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: accentColor }}>{effectivePresent}</span>
                      <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>/ {record.working_days} days</span>
                    </div>
                  )}
                  {selectedDates.size > 0 && (
                    <div style={{ fontSize: 12, color: '#b45309', fontWeight: 700, background: 'rgba(245,158,11,.1)', padding: '3px 10px', borderRadius: 6 }}>+{selectedDates.size} added</div>
                  )}
                  {deselectedDates.size > 0 && (
                    <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 700, background: 'rgba(239,68,68,.08)', padding: '3px 10px', borderRadius: 6 }}>−{deselectedDates.size} removed</div>
                  )}
                  {adjustmentEnabled && deductionAmount !== '' && !isNaN(+deductionAmount) && +deductionAmount !== 0 && (
                    <div style={{ fontSize: 12, color: +deductionAmount > 0 ? '#dc2626' : '#15803d', fontWeight: 700, background: +deductionAmount > 0 ? 'rgba(239,68,68,.08)' : 'rgba(34,197,94,.08)', padding: '3px 10px', borderRadius: 6 }}>
                      {+deductionAmount > 0 ? '−' : '+'}{fmt(Math.abs(+deductionAmount))} adjustment
                    </div>
                  )}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 5, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Net ~</span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: accentColor }}>
                      {fmt(recap.newNet - (adjustmentEnabled && deductionAmount !== '' && !isNaN(+deductionAmount) ? +deductionAmount : 0))}
                    </span>
                  </div>
                </div>
              )}

              <div className="pr-field">
                <label className="pr-label">Payment mode *</label>
                <select className="pr-select" value={form.payment_mode} onChange={setFormField('payment_mode')} style={{ width: '100%' }}>
                  <option value="">— Select —</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div className="pr-field">
                <label className="pr-label">Payment date *</label>
                <input type="date" className="pr-input" value={form.payment_date} onChange={setFormField('payment_date')} style={{ width: '100%' }} />
              </div>
              <div className="pr-field">
                <label className="pr-label">Transaction / cheque ref (optional)</label>
                <input type="text" className="pr-input" placeholder="UTR, cheque number…" value={form.transaction_ref} onChange={setFormField('transaction_ref')} style={{ width: '100%' }} />
              </div>
            </div>
            <div className="pr-modal-footer">
              <button className="pr-btn pr-btn--ghost" onClick={() => setStep(2)} disabled={saving}><IcoArrowL size={13} color="#1e40af" /> Back</button>
              <button className="pr-btn pr-btn--primary" style={{ background: accentGrad }} onClick={handleSave} disabled={saving}>
                {saving ? <IcoRefresh size={13} color="white" style={{ animation: 'pr-spin 1s linear infinite' }} /> : isEdit ? <IcoEdit size={13} color="white" /> : <IcoCheck size={13} color="white" />}
                {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Confirm payment'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Payslip detail drawer
// ─────────────────────────────────────────────────────────────────────────────
const PayslipDetail: React.FC<{ record: PayrollRecord; onPrint: (record: PayrollRecord) => void }> = ({ record: r, onPrint }) => {
  const Row: React.FC<{ label: string; value: string; color?: string; bold?: boolean; sub?: boolean }> =
    ({ label, value, color = '#374151', bold, sub }) => (
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: sub ? '3px 0 3px 16px' : '5px 0', borderTop: !sub ? '1px solid #f3f4f6' : 'none' }}>
        <span style={{ fontSize: sub ? 11 : 12, color: sub ? '#9ca3af' : '#6b7280', fontWeight: bold ? 700 : 500 }}>{label}</span>
        <span style={{ fontSize: sub ? 11 : 12, fontWeight: bold ? 800 : 600, color }}>{value}</span>
      </div>
    );
  return (
    <tr>
      <td colSpan={10} style={{ padding: 0, background: 'rgba(59,130,246,.025)' }}>
        <div style={{ padding: '14px 20px 14px 60px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24, animation: 'pr-fadeIn .2s ease', position: 'relative' }}>
          <button
            className="pr-btn pr-btn--ghost"
            style={{ position: 'absolute', top: 10, right: 20, padding: '5px 11px', fontSize: 11, background: 'rgba(59,130,246,.08)', color: '#1e40af', border: '1px solid rgba(59,130,246,.18)' }}
            onClick={() => onPrint(r)}
            title="Print payslip"
          >
            <IcoPrinter size={11} color="#1e40af" /> Print payslip
          </button>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Earnings</div>
            <Row label="Basic salary"  value={fmt(r.basic_salary)} />
            <Row label="HRA"           value={fmt(r.hra)} sub />
            <Row label="Transport"     value={fmt(r.transport_allowance)} sub />
            <Row label="Medical"       value={fmt(r.medical_allowance)} sub />
            <Row label="Other"         value={fmt(r.other_allowance)} sub />
            <Row label="Gross salary"  value={fmt(r.gross_salary)} color="#1e40af" bold />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Deductions</div>
            <Row label="Provident fund"   value={fmt(r.provident_fund)} />
            <Row label="Professional tax" value={fmt(r.professional_tax)} />
            <Row label="TDS"              value={fmt(r.tds)} />
            <Row label="Other deductions" value={fmt(r.other_deductions)} />
            <Row label="Total deductions" value={fmt(r.total_deductions)} color="#dc2626" bold />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Attendance</div>
            <Row label="Working days" value={String(r.working_days)} />
            <Row label="Present days" value={String(r.present_days)} />
            <Row label="Leave days"   value={String(r.leave_days)} />
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6 }}>Net</div>
              <Row label="Net salary" value={fmt(r.net_salary)} color="#1e40af" bold />
              {r.payment_date && <Row label="Paid on" value={r.payment_date} />}
              {r.payment_mode && <Row label="Via"     value={r.payment_mode.replace('_', ' ')} />}
              {r.transaction_ref && <Row label="Ref"  value={r.transaction_ref} />}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Ungenerated employee row — rendered directly inside the main table body
// (instead of a separate strip above it) so it reads as part of the same
// listing. Columns line up with the normal record row; cells that don't
// apply yet (gross/deductions/net/attendance/status) are collapsed into a
// single message cell, and the Actions column carries a "Generate" action
// instead of the usual set.
// ─────────────────────────────────────────────────────────────────────────────
const UngeneratedRow: React.FC<{
  emp: UngeneratedEmployee;
  onGenerate: (emp: UngeneratedEmployee) => void;
}> = ({ emp, onGenerate }) => (
  <tr style={{ background: 'rgba(239,68,68,.02)' }}>
    <td>
      <div style={{ width: 28, height: 28, background: '#fff', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IcoUserX size={12} color="#dc2626" />
      </div>
    </td>
    <td>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <Avatar src={emp.profile_picture ?? null} name={emp.employee_name} size={30} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
            {emp.employee_name ?? `#${emp.employee_id}`}
          </div>
          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>ID #{emp.employee_id}</div>
        </div>
      </div>
    </td>
    <td><span className="pr-role" style={{ background: 'rgba(239,68,68,.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,.18)' }}>{emp.employee_role}</span></td>
    <td colSpan={5} style={{ fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
      No payroll record generated for this month
    </td>
    <td>
      <span className="pr-badge" style={{ background: 'rgba(239,68,68,.08)', color: '#dc2626', border: '1px solid rgba(239,68,68,.2)' }}>
        <IcoAlert size={9} color="#dc2626" /> Not generated
      </span>
    </td>
    <td>
      <button
        className="pr-btn pr-btn--ghost"
        style={{ padding: '4px 10px', fontSize: 11, background: 'rgba(59,130,246,.08)', color: '#1e40af', border: '1px solid rgba(59,130,246,.18)' }}
        onClick={() => onGenerate(emp)}
        title="Generate payroll for this employee"
      >
        <IcoZap size={11} color="#1e40af" /> Generate
      </button>
    </td>
  </tr>
);

// ─────────────────────────────────────────────────────────────────────────────
// PayrollRecords
// No props needed — this component fully owns and manages its own Toast
// instance internally. Nothing external needs to pass a toast ref in.
// ─────────────────────────────────────────────────────────────────────────────
const PayrollRecords: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [activeTab,  setActiveTab]  = useState<RecordsTab>('records');
  const [records,    setRecords]    = useState<PayrollRecord[]>([]);
  const [notGenerated, setNotGenerated] = useState<UngeneratedEmployee[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    current_page: 1, total_pages: 1, total_items: 0, per_page: 20, has_next: false, has_previous: false,
  });

  const [filterMonth,   setFilterMonth]   = useState(THIS_MONTH);
  const [filterSession, setFilterSession] = useState(THIS_SESSION);
  const [filterRole,    setFilterRole]    = useState('');
  const [filterStatus,  setFilterStatus]  = useState('');
  const [search,        setSearch]        = useState('');
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(20);
  const [expandedId,    setExpandedId]    = useState<number | null>(null);
  const [markingPaid,   setMarkingPaid]   = useState<PayrollRecord | null>(null);
  const [editingPaid,   setEditingPaid]   = useState<PayrollRecord | null>(null);
  const [generateOpen,  setGenerateOpen]  = useState(false);
  const [prefillEmp,    setPrefillEmp]    = useState<UngeneratedEmployee | null>(null);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit: pageSize };
      if (filterMonth)   params.month_year     = filterMonth;
      if (filterSession) params.session        = filterSession;
      if (filterRole)    params.employee_role  = filterRole;
      if (filterStatus)  params.payment_status = filterStatus;
      if (search.trim()) params.search         = search.trim();
      const res = await apiClient.get('/payroll/records', { params });
      if (res.data.status === 'success') {
        setRecords(res.data.data.records ?? []);
        setPagination(res.data.data.pagination);
        setNotGenerated(res.data.data.not_generated ?? []);
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Failed to load records', life: 4000 });
    } finally { setLoading(false); }
  }, [page, pageSize, filterMonth, filterSession, filterRole, filterStatus, search]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this payroll record? This cannot be undone.')) return;
    try {
      const res = await apiClient.delete(`/payroll/records/${id}`);
      if (res.data.status === 'success') {
        toast.current?.show({ severity: 'success', summary: 'Deleted', detail: 'Record deleted.', life: 3000 });
        fetchRecords();
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: res.data.message, life: 4000 });
      }
    } catch (e: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: e?.response?.data?.message || 'Delete failed', life: 4000 });
    }
  };

  const handlePrintPayslip = (record: PayrollRecord) => {
    printHtml(buildPayslipHtml(record, undefined, { colorMode: 'color' }));
  };

  const handlePrintRegister = () => {
    if (records.length === 0) {
      toast.current?.show({ severity: 'warn', summary: 'Nothing to print', detail: 'No records match the current filters.', life: 3000 });
      return;
    }
    printHtml(buildPayrollRegisterHtml(records, filterMonth, filterSession, undefined, { colorMode: 'color' }));
  };

  const totalNet   = records.reduce((a, r) => a + Number(r.net_salary),  0);
  const totalGross = records.reduce((a, r) => a + Number(r.gross_salary), 0);
  const paidCount  = records.filter(r => r.payment_status === 'paid').length;
  const pendCount  = records.filter(r => r.payment_status === 'pending').length;

  const pageNums = () => {
    const total = pagination.total_pages, cur = pagination.current_page;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i);
    if (cur < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  };

  const clearFilters = () => {
    setFilterMonth(THIS_MONTH); setFilterSession(THIS_SESSION);
    setFilterRole(''); setFilterStatus(''); setSearch(''); setPage(1);
  };

  const openGenerateFor = (emp: UngeneratedEmployee) => {
    setPrefillEmp(emp);
    setGenerateOpen(true);
  };

  return (
    <>
      <style>{CSS}</style>
      <style>{SHELL_CSS}</style>
      <style>{`
        .pr-filter-bar {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .pr-filter-bar .pr-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-bottom: 0;
        }
        .pr-filter-bar .pr-label {
          height: 14px;
          line-height: 14px;
          display: flex;
          align-items: center;
        }
        .pr-filter-bar .pr-input,
        .pr-filter-bar .pr-select {
          height: 36px;
          box-sizing: border-box;
          display: flex;
          align-items: center;
        }
        .pr-filter-bar .pr-filter-tag {
          display: flex;
          align-items: center;
          gap: 6px;
          height: 36px;
        }
        .pr-filter-bar .pr-filter-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-left: auto;
        }
        .pr-filter-bar .pr-filter-actions .pr-btn {
          height: 36px;
          box-sizing: border-box;
        }
      `}</style>
      <Toast ref={toast} position="top-right" />

      <GlobalHeaderTitle
        title="Payroll Records"
        subtitle={activeTab === 'records'
          ? (loading ? 'Loading…' : `${pagination.total_items} records`)
          : 'Salary advances'}
        icon="pi pi-wallet"
        buttons={[
          { label: 'Refresh', icon: 'pi pi-refresh', onClick: fetchRecords, outlined: true, disabled: loading },
        ]}
      />

      {/* Records / Advances switch */}
      <div style={{ fontFamily: 'system-ui,-apple-system,sans-serif' }}>
      <div className="pr-tab-bar" style={{ marginBottom: 16 }}>
        <button
          className={`pr-tab${activeTab === 'records' ? ' pr-tab--active' : ''}`}
          onClick={() => setActiveTab('records')}
        >
          <IcoList size={13} /> Payroll Records
        </button>
        <button
          className={`pr-tab pr-tab--advances${activeTab === 'advances' ? ' pr-tab--active' : ''}`}
          onClick={() => setActiveTab('advances')}
        >
          <IcoDollar size={13} /> Advances
        </button>
      </div>

      {activeTab === 'advances' ? (
        <PayrollAdvances toast={toast} />
      ) : (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12, marginBottom: 16 }}>
            {[
              { label: 'Records shown', val: pagination.total_items, color: '#1e40af', bg: 'rgba(59,130,246,.08)' },
              { label: 'Gross payable', val: fmt(totalGross),        color: '#1d4ed8', bg: 'rgba(29,78,216,.08)'  },
              { label: 'Net payable',   val: fmt(totalNet),          color: '#1e40af', bg: 'rgba(59,130,246,.08)' },
              { label: 'Paid',          val: paidCount,              color: '#15803d', bg: 'rgba(34,197,94,.08)'  },
              { label: 'Pending',       val: pendCount,              color: '#b45309', bg: 'rgba(245,158,11,.08)' },
            ].map((s, i) => (
              <div key={i} style={{ padding: '12px 16px', borderRadius: 14, border: '1px solid #e5e7eb', background: s.bg, animation: `pr-slideUp .3s ease ${i * 0.05}s both` }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="pr-filter-bar" style={{ background: 'white', borderRadius: 16, padding: '14px 18px', marginBottom: 14, border: '1px solid #e5e7eb' }}>
            <div className="pr-filter-tag" style={{ flexShrink: 0 }}>
              <IcoFilter size={13} color="#1e40af" />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '.5px' }}>Filters</span>
            </div>
            <div className="pr-field">
              <label className="pr-label">Search</label>
              <input type="text" className="pr-input" placeholder="Employee name…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ width: 170 }} />
            </div>
            <div className="pr-field">
              <label className="pr-label">Month</label>
              <input type="month" className="pr-input" value={filterMonth} onChange={e => { setFilterMonth(e.target.value); setPage(1); }} style={{ width: 160 }} />
            </div>
            <div className="pr-field">
              <label className="pr-label">Session</label>
              <input type="text" className="pr-input" placeholder="e.g. 2025-26" value={filterSession} onChange={e => { setFilterSession(e.target.value); setPage(1); }} style={{ width: 120 }} />
            </div>
            <div className="pr-field">
              <label className="pr-label">Role</label>
              <select className="pr-select" value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }} style={{ width: 140 }}>
                <option value="">All roles</option>
                <option value="teacher">Teacher</option>
                <option value="staff">Staff</option>
                <option value="principal">Principal</option>
              </select>
            </div>
            <div className="pr-field">
              <label className="pr-label">Status</label>
              <select className="pr-select" value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }} style={{ width: 140 }}>
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="on_hold">On hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="pr-filter-actions">
              <button
                className="pr-btn pr-btn--primary"
                style={{ padding: '0 14px', background: 'linear-gradient(135deg,#1e40af,#3b82f6)', boxShadow: '0 3px 10px rgba(59,130,246,.28)' }}
                onClick={() => { setPrefillEmp(null); setGenerateOpen(true); }}
              >
                <IcoZap size={13} color="white" /> Generate Payroll
              </button>
              <button
                className="pr-btn pr-btn--ghost"
                style={{ padding: '0 12px', background: 'rgba(59,130,246,.07)', color: '#1e40af', border: '1px solid rgba(59,130,246,.15)' }}
                onClick={handlePrintRegister}
                title="Print payroll register"
              >
                <IcoPrinter size={13} color="#1e40af" /> Print
              </button>
              <button className="pr-btn pr-btn--ghost" style={{ padding: '0 12px', background: 'rgba(59,130,246,.07)', color: '#1e40af', border: '1px solid rgba(59,130,246,.15)' }} onClick={clearFilters} title="Clear"><IcoX size={13} color="#1e40af" /></button>
              <button className="pr-btn pr-btn--ghost" style={{ padding: '0 12px', background: 'rgba(59,130,246,.07)', color: '#1e40af', border: '1px solid rgba(59,130,246,.15)' }} onClick={fetchRecords} title="Refresh">
                <IcoRefresh size={13} color="#1e40af" style={{ animation: loading ? 'pr-spin 1s linear infinite' : 'none' }} />
              </button>
            </div>
          </div>

          {/* Table */}
          <div style={{ background: 'white', borderRadius: 20, border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 2px 16px rgba(59,130,246,.07)' }}>
            <div style={{ padding: '13px 20px', borderBottom: '1px solid #f3f4f6', background: 'linear-gradient(135deg,#fff,#fafbfc)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(59,130,246,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IcoList size={14} color="#1e40af" />
                </div>
                <span style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  {loading ? 'Loading…' : `${pagination.total_items} records`}
                  {notGenerated.length > 0 && !loading && (
                    <span style={{ color: '#dc2626', marginLeft: 6 }}>· {notGenerated.length} not generated</span>
                  )}
                  {filterMonth && <span style={{ color: '#3b82f6', marginLeft: 6 }}>for {fmtMonth(filterMonth)}</span>}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>Per page</span>
                <select className="pr-select" value={pageSize} style={{ padding: '5px 8px', fontSize: 12 }} onChange={e => { setPageSize(+e.target.value); setPage(1); }}>
                  {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>

            <div className="pr-scroll" style={{ overflowX: 'auto' }}>
              <table className="pr-table">
                <thead>
                  <tr>
                    <th>#</th><th>Employee</th><th>Role</th><th>Month</th>
                    <th>Gross</th><th>Deductions</th><th>Net salary</th>
                    <th>Attendance</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} i={i} cols={10} />)
                    : records.length === 0 && notGenerated.length === 0
                      ? (
                        <tr><td colSpan={10}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 12, animation: 'pr-fadeIn .3s ease' }}>
                            <div style={{ width: 54, height: 54, borderRadius: 14, background: 'rgba(59,130,246,.06)', border: '1.5px dashed rgba(59,130,246,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <IcoDollar size={24} color="#93c5fd" />
                            </div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#374151' }}>No payroll records</div>
                            <div style={{ fontSize: 13, color: '#9ca3af' }}>Generate payroll from the Salaries tab first.</div>
                          </div>
                        </td></tr>
                      )
                      : (
                        <>
                          {/* Ungenerated employees listed first, inline as part of the same table */}
                          {!loading && notGenerated.map(emp => (
                            <UngeneratedRow key={`ug-${emp.employee_role}-${emp.employee_id}`} emp={emp} onGenerate={openGenerateFor} />
                          ))}

                          {records.map((rec, i) => {
                            const isExpanded = expandedId === rec.id;
                            const attendPct  = rec.working_days > 0 ? Math.round((rec.present_days / rec.working_days) * 100) : 0;
                            return (
                              <React.Fragment key={rec.id}>
                                <tr style={{ animationDelay: `${i * 0.04}s`, background: isExpanded ? 'rgba(59,130,246,.025)' : undefined }}>
                                  <td>
                                    <div style={{ width: 28, height: 28, background: '#f9fafb', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#6b7280', border: '1px solid #e5e7eb' }}>
                                      {(page - 1) * pageSize + i + 1}
                                    </div>
                                  </td>
                                  <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                                      <Avatar src={rec.profile_picture ?? null} name={rec.employee_name} size={30} />
                                      <div style={{ minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                                          {rec.employee_name ?? `#${rec.employee_id}`}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>ID #{rec.employee_id}</div>
                                      </div>
                                    </div>
                                  </td>
                                  <td><span className="pr-role" style={{ background: 'rgba(59,130,246,.08)', color: '#1e40af', border: '1px solid rgba(59,130,246,.18)' }}>{rec.employee_role}</span></td>
                                  <td style={{ fontSize: 13, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                                    {fmtMonth(rec.month_year)}
                                    <div style={{ fontSize: 11, color: '#9ca3af' }}>{rec.session}</div>
                                  </td>
                                  <td><div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>{fmt(rec.gross_salary)}</div></td>
                                  <td><div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626' }}>−{fmt(rec.total_deductions)}</div></td>
                                  <td><div style={{ fontSize: 14, fontWeight: 800, color: '#1e40af' }}>{fmt(rec.net_salary)}</div></td>
                                  <td>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                                      {rec.present_days}/{rec.working_days}
                                      <span style={{ fontSize: 11, color: attendPct >= 75 ? '#15803d' : '#dc2626', marginLeft: 5 }}>({attendPct}%)</span>
                                    </div>
                                    <div style={{ height: 3, borderRadius: 2, background: '#f3f4f6', marginTop: 4, width: 70 }}>
                                      <div style={{ height: '100%', borderRadius: 2, width: `${attendPct}%`, background: attendPct >= 75 ? '#22c55e' : '#ef4444', transition: 'width .5s' }} />
                                    </div>
                                  </td>
                                  <td><StatusBadge status={rec.payment_status} /></td>
                                  <td>
                                    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                                      {/* Print payslip */}
                                      <button className="pr-btn pr-btn--ghost" style={{ padding: '4px 8px' }} onClick={() => handlePrintPayslip(rec)} title="Print payslip">
                                        <IcoPrinter size={12} color="#1e40af" />
                                      </button>
                                      {/* Expand */}
                                      <button className="pr-btn pr-btn--ghost" style={{ padding: '4px 8px' }} onClick={() => setExpandedId(isExpanded ? null : rec.id)} title="Details">
                                        <IcoChevDown size={12} color="#1e40af" style={{ transition: 'transform .2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }} />
                                      </button>
                                      {/* Mark paid */}
                                      {(rec.payment_status === 'pending' || rec.payment_status === 'on_hold') && (
                                        <button className="pr-btn pr-btn--ghost" style={{ padding: '4px 8px' }} onClick={() => setMarkingPaid(rec)} title="Mark paid">
                                          <IcoCheck size={12} color="#15803d" />
                                        </button>
                                      )}
                                      {/* Edit — paid only */}
                                      {rec.payment_status === 'paid' && (
                                        <button className="pr-btn pr-btn--ghost" style={{ padding: '4px 8px', border: '1px solid rgba(109,28,209,.2)', background: 'rgba(109,28,209,.06)' }} onClick={() => setEditingPaid(rec)} title="Edit payment">
                                          <IcoEdit size={12} color="#6d28d9" />
                                        </button>
                                      )}
                                      {/* Delete */}
                                      {(rec.payment_status === 'pending' || rec.payment_status === 'on_hold') && (
                                        <button className="pr-btn pr-btn--danger" style={{ padding: '4px 8px' }} onClick={() => handleDelete(rec.id)} title="Delete">
                                          <IcoTrash size={12} color="#dc2626" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {isExpanded && <PayslipDetail record={rec} onPrint={handlePrintPayslip} />}
                              </React.Fragment>
                            );
                          })}
                        </>
                      )
                  }
                </tbody>
              </table>
            </div>

            {pagination.total_pages > 1 && (
              <div style={{ padding: '13px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 500 }}>
                  Page {pagination.current_page} of {pagination.total_pages} · {pagination.total_items} total
                </span>
                <div style={{ display: 'flex', gap: 5 }}>
                  <button className="pr-pg-btn" disabled={!pagination.has_previous} onClick={() => setPage(p => p - 1)}><IcoChevL size={13} color="#374151" /></button>
                  {pageNums().map((n, i) =>
                    n === '...'
                      ? <span key={i} style={{ width: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>…</span>
                      : <button
                          key={i}
                          className="pr-pg-btn"
                          style={n === pagination.current_page ? { background: 'linear-gradient(135deg,#1e40af,#3b82f6)', color: 'white', borderColor: 'transparent' } : undefined}
                          onClick={() => setPage(n as number)}
                        >{n}</button>
                  )}
                  <button className="pr-pg-btn" disabled={!pagination.has_next} onClick={() => setPage(p => p + 1)}><IcoChevR size={13} color="#374151" /></button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
      </div>

      {/* Mark-paid modal */}
      {markingPaid && (
        <PaymentModal
          record={markingPaid}
          isEdit={false}
          onDone={() => { setMarkingPaid(null); fetchRecords(); }}
          onClose={() => setMarkingPaid(null)}
          toast={toast}
        />
      )}

      {/* Edit-paid modal */}
      {editingPaid && (
        <PaymentModal
          record={editingPaid}
          isEdit={true}
          onDone={() => { setEditingPaid(null); fetchRecords(); }}
          onClose={() => setEditingPaid(null)}
          toast={toast}
        />
      )}

      {/* Generate payroll modal */}
      <GeneratePayrollModal
        open={generateOpen}
        onClose={() => { setGenerateOpen(false); setPrefillEmp(null); }}
        onGenerated={() => { setGenerateOpen(false); setPrefillEmp(null); fetchRecords(); }}
        toast={toast}
        prefillEmployee={prefillEmp ?? undefined}
      />
    </>
  );
};

export default PayrollRecords;